import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { fmtBRL, fmtNum, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Download, Filter, RotateCcw, Save, Trash2, FileBarChart2, Search, Calendar, ListFilter, Plus, FileText, Printer, ShieldCheck, Database, Target, Zap, Activity, History, Layers, ArrowRight } from "lucide-react";
import type { NotaSimplificada, NotaStatus } from "@/lib/fiscal-types";
import { NotaDetailModal } from "@/components/NotaDetailModal";
import { StatusBadge } from "@/components/StatusBadge";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { cn } from "@/lib/utils";

type SortKey = "emissao" | "emitente" | "destinatario" | "numero" | "valorOficial" | "vICMS" | "notaStatus";

interface Filtros {
  dataInicial: string; dataFinal: string; tipoData: "emissao";
  fCliente: boolean; cliente: string; fEmitente: boolean; emitente: string;
  fSerie: boolean; serie: string; fModelo: boolean; modelo: "55" | "65" | "";
  fSituacao: boolean; situacao: NotaStatus | "";
  fValorMin: boolean; valorMin: string; fValorMax: boolean; valorMax: string;
  ordenarPor: SortKey; ascendente: boolean; pageSize: number;
}

const STORAGE_KEY = "relatorio-notas-filtros";
const PAGE_SIZES = [25, 50, 100, 200];

const filtrosIniciais: Filtros = {
  dataInicial: "", dataFinal: "", tipoData: "emissao",
  fCliente: false, cliente: "", fEmitente: false, emitente: "",
  fSerie: false, serie: "", fModelo: false, modelo: "",
  fSituacao: false, situacao: "", fValorMin: false, valorMin: "", fValorMax: false, valorMax: "",
  ordenarPor: "emissao", ascendente: false, pageSize: 25,
};

function parseEmissao(s: string): number {
  if (!s) return 0;
  if (s.includes("/")) {
    const [d, m, y] = s.split(" ")[0].split("/");
    return new Date(`${y}-${m}-${d}`).getTime();
  }
  return new Date(s).getTime();
}

function emissaoISO(s: string): string {
  if (!s) return "";
  if (s.includes("/")) {
    const [d, m, y] = s.split(" ")[0].split("/");
    return `${y}-${m}-${d}`;
  }
  return s.slice(0, 10);
}

export default function RelatorioPage() {
  const filteredNotas = useFiscalStore((s) => s.filteredNotas);
  const { user } = useAuth();
  const [f, setF] = useState<Filtros>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...filtrosIniciais, ...JSON.parse(raw) };
    } catch {/* ignore */ }
    return filtrosIniciais;
  });
  const [page, setPage] = useState(1);
  const [modelos, setModelos] = useState<{ id: string; nome: string; configuracao: Filtros }[]>([]);
  const [novoNome, setNovoNome] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(f)); }, [f]);
  useEffect(() => { loadModelos(); }, [user?.id]);

  async function loadModelos() {
    if (!user) return;
    const { data, error } = await supabase.from("modelos_relatorio").select("id, nome, configuracao").order("created_at", { ascending: false });
    if (!error) setModelos((data || []) as any);
  }

  async function salvarModelo() {
    if (!user) { toast.error("Faça login para salvar"); return; }
    const nome = novoNome.trim();
    if (!nome) { toast.error("Informe um nome"); return; }
    const { error } = await supabase.from("modelos_relatorio").insert({ user_id: user.id, nome, configuracao: f as any });
    if (!error) { toast.success("Modelo salvo"); setNovoNome(""); loadModelos(); }
  }

  const filtradas = useMemo(() => {
    const ini = f.dataInicial ? new Date(f.dataInicial).getTime() : -Infinity;
    const fim = f.dataFinal ? new Date(f.dataFinal).getTime() + 86_399_999 : Infinity;
    const vMin = f.fValorMin && f.valorMin !== "" ? Number(f.valorMin) : -Infinity;
    const vMax = f.fValorMax && f.valorMax !== "" ? Number(f.valorMax) : Infinity;
    const cliente = f.fCliente ? f.cliente.trim().toLowerCase() : "";
    const emitente = f.fEmitente ? f.emitente.trim().toLowerCase() : "";
    const out = filteredNotas.filter(n => {
      const t = parseEmissao(n.emissao);
      if (t < ini || t > fim) return false;
      const v = n.valorOficial || 0;
      if (v < vMin || v > vMax) return false;
      if (cliente && !(n.destinatario || "").toLowerCase().includes(cliente)) return false;
      if (emitente && !(n.emitente || "").toLowerCase().includes(emitente)) return false;
      if (f.fSerie && String(n.serie) !== f.serie) return false;
      if (f.fModelo && f.modelo && n.modelo !== Number(f.modelo)) return false;
      if (f.fSituacao && f.situacao && n.notaStatus !== f.situacao) return false;
      return true;
    });
    const dir = f.ascendente ? 1 : -1;
    out.sort((a, b) => {
      const av: any = f.ordenarPor === "emissao" ? parseEmissao(a.emissao) : (a as any)[f.ordenarPor];
      const bv: any = f.ordenarPor === "emissao" ? parseEmissao(b.emissao) : (b as any)[f.ordenarPor];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? ""), "pt-BR") * dir;
    });
    return out;
  }, [filteredNotas, f]);

  useEffect(() => { setPage(1); }, [f]);

  const totals = useMemo(() => {
    let valor = 0, icms = 0, pis = 0, cofins = 0;
    filtradas.forEach(n => {
      valor += n.valorOficial || 0; icms += n.vICMS || 0; pis += n.vPIS || 0; cofins += n.vCOFINS || 0;
    });
    return { qtd: filtradas.length, valor, icms, pis, cofins };
  }, [filtradas]);

  const totalPages = Math.max(1, Math.ceil(filtradas.length / f.pageSize));
  const pageData = filtradas.slice((page - 1) * f.pageSize, page * f.pageSize);

  function exportarCSV() {
    if (!filtradas.length) return;
    const headers = ["Chave", "Modelo", "Numero", "Serie", "Emissao", "Emitente", "CNPJ Emit", "Destinatario", "Valor Oficial", "ICMS", "PIS", "COFINS", "Situacao"];
    const rows = filtradas.map(n => [n.chave, n.modelo, n.numero, n.serie, emissaoISO(n.emissao), n.emitente, n.cnpjEmit, n.destinatario, (n.valorOficial || 0).toFixed(2), (n.vICMS || 0).toFixed(2), (n.vPIS || 0).toFixed(2), (n.vCOFINS || 0).toFixed(2), n.notaStatus]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `relatorio-notas-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  function exportarPDF() {
    if (!filtradas.length) return;
    const doc = new jsPDF("l", "mm", "a4");
    const m = 10;
    const contentW = doc.internal.pageSize.width - (m * 2);

    doc.setFillColor(245, 245, 245); doc.roundedRect(m, m, contentW, 20, 2, 2, "F");
    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.text("RELATÓRIO CONSOLIDADO DE DOCUMENTOS DE DADOS", m + 5, m + 10);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text(`Extraído em: ${new Date().toLocaleString("pt-BR")}`, m + 5, m + 16);
    doc.text(`Período: ${f.dataInicial || "Início"} até ${f.dataFinal || "Fim"}`, contentW - 50, m + 16);

    autoTable(doc, {
      startY: 35,
      theme: "grid",
      head: [["EMISSÃO", "NÚMERO", "SÉRIE", "EMITENTE", "DESTINATÁRIO", "VALOR TOTAL", "ICMS", "PIS", "COFINS", "STATUS"]],
      body: filtradas.map(n => [
        n.emissao?.split(" ")[0], n.numero, n.serie,
        (n.emitente || "").slice(0, 30), (n.destinatario || "").slice(0, 30),
        fmtBRL(n.valorOficial || 0), fmtBRL(n.vICMS || 0), fmtBRL(n.vPIS || 0), fmtBRL(n.vCOFINS || 0), n.notaStatus
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [26, 102, 204], textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: { 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFillColor(240, 240, 240); doc.roundedRect(m, finalY, contentW, 15, 2, 2, "F");
    doc.setFontSize(9); doc.setFont("helvetica", "bold");
    doc.text(`TOTAL DE NOTAS: ${totals.qtd}`, m + 5, finalY + 9);
    doc.text(`VALOR TOTAL: ${fmtBRL(totals.valor)}`, m + 60, finalY + 9);
    doc.text(`ICMS TOTAL: ${fmtBRL(totals.icms)}`, m + 130, finalY + 9);
    doc.text(`PIS/COFINS: ${fmtBRL(totals.pis + totals.cofins)}`, contentW - 60, finalY + 9);

    doc.save(`relatorio_dados_${new Date().getTime()}.pdf`);
    toast.success("Relatório PDF gerado com sucesso.");
  }

  if (!filteredNotas.length) {
    return (
      <div className="p-8 max-w-4xl mx-auto animate-in fade-in zoom-in duration-1000">
        <div className="glass-card py-32 text-center space-y-8 border-primary/10">
          <div className="size-32 rounded-3xl glass border-primary/20 flex items-center justify-center mx-auto mb-8 shadow-2xl rotate-3">
            <FileBarChart2 className="size-16 text-primary animate-pulse" />
          </div>
          <h2 className="text-4xl font-black text-foreground tracking-tighter">Zero Telemetry Data</h2>
          <p className="text-muted-foreground max-w-md mx-auto text-lg font-medium">Faça o upload dos seus arquivos XML para desbloquear o Hub de Inteligência e relatórios avançados.</p>
          <Button className="h-16 rounded-2xl bg-primary text-white hover:bg-primary/90 transition-all font-black text-xs uppercase tracking-widest px-10 shadow-2xl shadow-primary/20" onClick={() => window.location.href = '/upload'}>
            INICIAR INGESTÃO DE DADOS
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-1000">
      
      {/* Executive Command Header */}
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Target className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Executive Command Summary</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Relatório <span className="text-primary">Consolidado</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
            <span className="flex items-center gap-2 uppercase tabular-nums font-black"><Layers className="size-3.5 text-primary" /> {fmtNum(filtradas.length)} DOCUMENTOS FILTRADOS</span>
            <span className="flex items-center gap-2 uppercase tabular-nums font-black text-emerald-500"><ShieldCheck className="size-3.5" /> ANALYTICAL SYNC OK</span>
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Button variant="outline" size="lg" onClick={() => setF(filtrosIniciais)} className="h-14 rounded-2xl bg-white/5 border-foreground/5 hover:bg-primary/5 hover:border-primary/20 transition-all font-black text-[10px] uppercase tracking-widest px-6 group">
            <RotateCcw className="size-4 mr-2 group-hover:rotate-180 transition-transform duration-500" /> RESETAR
          </Button>
          <Button variant="outline" size="lg" onClick={exportarCSV} className="h-14 rounded-2xl bg-white/5 border-foreground/5 hover:bg-emerald-500/5 hover:border-emerald-500/20 transition-all font-black text-[10px] uppercase tracking-widest px-6">
            <Download className="size-4 mr-2" /> CSV
          </Button>
          <Button size="lg" onClick={exportarPDF} className="h-14 rounded-2xl bg-primary text-white hover:bg-primary/90 transition-all font-black text-[10px] uppercase tracking-widest px-8 shadow-2xl shadow-primary/30">
            <Printer className="size-4 mr-2" /> GERAR PDF
          </Button>
        </div>
      </header>
      {/* Filter & Command Matrix */}
      <div className="glass-card p-6 border-primary/5 bg-white/[0.01]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Calendar className="size-3 text-primary" /> Período Inicial
            </Label>
            <Input type="date" value={f.dataInicial} onChange={e => setF({ ...f, dataInicial: e.target.value })} className="h-11 rounded-xl bg-white/5 border-foreground/5 focus:border-primary/50 text-[11px] font-bold" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Calendar className="size-3 text-primary" /> Período Final
            </Label>
            <Input type="date" value={f.dataFinal} onChange={e => setF({ ...f, dataFinal: e.target.value })} className="h-11 rounded-xl bg-white/5 border-foreground/5 focus:border-primary/50 text-[11px] font-bold" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Search className="size-3 text-primary" /> Destinatário/Cliente
            </Label>
            <div className="flex gap-2">
              <Checkbox checked={f.fCliente} onCheckedChange={v => setF({ ...f, fCliente: !!v })} className="size-5 rounded-md mt-3" />
              <Input placeholder="Filtrar por nome..." value={f.cliente} onChange={e => setF({ ...f, cliente: e.target.value })} disabled={!f.fCliente} className="h-11 rounded-xl bg-white/5 border-foreground/5 focus:border-primary/50 text-[11px] font-bold" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <ListFilter className="size-3 text-primary" /> Situação Operacional
            </Label>
            <div className="flex gap-2">
              <Checkbox checked={f.fSituacao} onCheckedChange={v => setF({ ...f, fSituacao: !!v })} className="size-5 rounded-md mt-3" />
              <Select value={f.situacao} onValueChange={v => setF({ ...f, situacao: v as any })} disabled={!f.fSituacao}>
                <SelectTrigger className="h-11 rounded-xl bg-white/5 border-foreground/5 focus:border-primary/50 text-[11px] font-bold">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="bg-background/95 backdrop-blur-xl border-primary/20">
                  <SelectItem value="AUTORIZADA">Autorizada</SelectItem>
                  <SelectItem value="CANCELADA">Cancelada</SelectItem>
                  <SelectItem value="CONTINGENCIA">Contingência</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-end">
             <div className="glass-technical p-3 w-full flex items-center justify-between border-primary/10 bg-primary/5">
                <span className="text-[9px] font-black uppercase tracking-widest text-primary">Consolidado</span>
                <span className="text-sm font-black tabular-nums">{fmtBRL(totals.valor)}</span>
             </div>
          </div>
        </div>
      </div>

      {/* Main Data Table Matrix */}
      <div className="glass-card border-foreground/5 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/10">
            <TableRow className="hover:bg-transparent border-foreground/5">
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6">Data</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6">Número/Série</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6">Emitente</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6 text-right">Valor Total</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6 text-right">ICMS</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6 text-right">PIS/COF</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6 text-center">Status</TableHead>
              <TableHead className="text-[10px] font-black uppercase tracking-widest py-6 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageData.map((n, idx) => (
              <TableRow 
                key={n.chave} 
                className="border-foreground/5 hover:bg-white/[0.02] transition-colors group cursor-pointer"
                onClick={() => setOpenIndex((page - 1) * f.pageSize + idx)}
              >
                <TableCell className="text-[11px] font-bold tabular-nums opacity-80">{fmtDate(n.emissao)}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] font-black tracking-tight">{n.numero}</span>
                    <span className="text-[8px] font-black uppercase text-muted-foreground tracking-widest">Série {n.serie} · Mod {n.modelo}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] font-bold truncate max-w-[200px]">{n.emitente || "—"}</span>
                    <span className="text-[9px] font-medium text-muted-foreground tabular-nums">{n.cnpjEmit || "—"}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right text-[11px] font-black tabular-nums">{fmtBRL(n.valorOficial)}</TableCell>
                <TableCell className="text-right text-[11px] font-bold tabular-nums text-primary/80">{fmtBRL(n.vICMS)}</TableCell>
                <TableCell className="text-right text-[11px] font-bold tabular-nums text-muted-foreground">{fmtBRL((n.vPIS || 0) + (n.vCOFINS || 0))}</TableCell>
                <TableCell className="text-center">
                  <StatusBadge status={n.notaStatus} size="sm" />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => setOpenIndex(filtradas.indexOf(n))} className="size-8 rounded-lg hover:bg-primary/10 hover:text-primary transition-all">
                    <ArrowRight className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination Console */}
        <div className="p-6 border-t border-foreground/5 flex items-center justify-between bg-muted/5">
          <div className="flex items-center gap-6">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Página {page} de {totalPages}</span>
            <div className="flex items-center gap-2">
               <Select value={String(f.pageSize)} onValueChange={v => setF({ ...f, pageSize: Number(v) })}>
                  <SelectTrigger className="h-8 w-20 rounded-lg bg-white/5 border-foreground/5 text-[10px] font-black">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background/95 border-primary/20">
                    {PAGE_SIZES.map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
               </Select>
               <span className="text-[9px] font-bold text-muted-foreground uppercase">Registros por página</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} className="h-10 px-6 rounded-xl border-foreground/5 hover:border-primary/30 font-black text-[10px] uppercase tracking-widest">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)} className="h-10 px-6 rounded-xl border-foreground/5 hover:border-primary/30 font-black text-[10px] uppercase tracking-widest">Próxima</Button>
          </div>
        </div>
      </div>

      {openIndex !== null && (
        <NotaDetailModal 
          notas={filtradas} 
          index={openIndex}
          onClose={() => setOpenIndex(null)} 
          onChangeIndex={setOpenIndex}
        />
      )}
    </div>
  );
}
